import { cn } from '@/lib/utils'
import { Typo3SectionSpotSanityContentText } from '@/types/typo3'
import { Button, Container, Richtext, Section, Title } from '@/ui/components'
import { getTitleTag } from '@/utils'
import { memo } from 'react'
import Contacts from './Components/Contacts'
import ImageSlide from './Components/ImageSlide'
import Links from './Components/Links'
import Tips from './Components/Tips'
import Video from './Components/Video'

// TODO: Better to migrate to SectionText?

interface Props {
  data: Typo3SectionSpotSanityContentText
}

const SpotSanityContentText = ({ data }: Props) => {
  const c = data.content
  const isInfo =
    c.contacts?.length ||
    c.files?.length ||
    c.links?.length ||
    c.tips?.length ||
    undefined

  const titleTag = getTitleTag(c?.titleLayout)

  return (
    <Section data={data} className="s-content-text">
      <Container anim="fade-in" className={''}>
        <div className="gap-gutter grid grid-cols-1 md:grid-cols-3">
          <div
            className={cn(
              c?.layout === 'text-two-columns'
                ? 'md:col-span-3'
                : 'md:col-span-2'
            )}
          >
            <div
              className={cn(
                'gap-lg flex flex-col',
                c.layout !== 'text-two-columns' && 'max-w-180'
              )}
            >
              {c?.title && c.titleLayout !== '100' && (
                <div className={'head'}>
                  <Title as={titleTag} className={titleTag}>
                    {c?.title}
                  </Title>
                </div>
              )}
              {c?.summary && <Richtext text={c?.summary as string} />}
              <Richtext
                text={c?.text as string}
                className={cn(
                  c.layout === 'text-two-columns' && 'md:columns-2'
                )}
              />
              {!!c?.buttons?.length && (
                <div className={'gap-sm flex flex-wrap'}>
                  {c?.buttons?.map((b, i) => {
                    const isNextLink =
                      b.link?.target === '_blank' ? false : true
                    return (
                      <Button
                        key={i?.toString()}
                        href={b.link?.href}
                        variant="secondary"
                        icon={b.icon ? b.icon : undefined}
                        iconPosition={
                          b.iconPosition ? b.iconPosition : undefined
                        }
                        target={
                          isNextLink ? undefined : (b?.link?.target as string)
                        }
                        nextLink={isNextLink}
                      >
                        {b.link?.title}
                      </Button>
                    )
                  })}
                </div>
              )}
              {!!c?.files?.length && (
                <div className={'gap-sm flex'}>
                  {c?.files?.map((f, i) => (
                    <Button
                      key={i?.toString()}
                      href={f.publicUrl}
                      variant="text"
                      icon="material-symbols:download-sharp"
                      target="_blank"
                      nextLink={false}
                      iconPosition="left"
                    >
                      {f.properties?.title}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isInfo && (
            <div className={''}>
              <div className="gap-gutter top-gutter sticky flex flex-col">
                {c?.tips && c?.tips?.length && <Tips data={c.tips} />}
                {c?.contacts && <Contacts data={c.contacts} />}
                <Links links={c.links} files={c.files} />
              </div>
            </div>
          )}
          {c?.images && c?.images?.length ? (
            <div className={'md:col-span-3'}>
              <ImageSlide data={c.images} />
            </div>
          ) : null}

          {c?.videos && c?.videos.length
            ? c?.videos?.map((video, index) => {
                const provider =
                  video?.properties?.extension === 'youtube' ||
                  video?.properties?.extension === 'vimeo'
                    ? video?.properties?.extension
                    : 'html5'

                return (
                  video?.publicUrl &&
                  provider && (
                    <div key={index?.toString()} className={'md:col-span-3'}>
                      <Video video={video} provider={provider} />
                    </div>
                  )
                )
              })
            : null}
        </div>
      </Container>
    </Section>
  )
}

export default memo(SpotSanityContentText)
